import { AuthenticationService } from 'src/app/core/services/auth.service';
import { AccessGuids, ConfigSystemParams, FlatpickrFormat, StorageValues, WriteToTemplateDelay } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CompanySelectEvent } from 'src/app/core/interfaces/events/event-select-company';
import { ComponentAccessUserService } from 'src/app/core/services/utelize/system/component-access/component-access-user-service';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { DatePipe } from '@angular/common';
import { DecryptAesClientSide, EncryptAesClientSide } from 'src/app/core/encryption';
import { DropdownService } from 'src/app/core/services/utelize/general/dropdown-service';
import { environment } from 'src/environments/environment';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { finalize, Subject, takeUntil } from 'rxjs';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { PlatformInviewService } from 'src/app/core/services/utelize/general/platform-inview-service';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { StatementScope } from 'src/app/core/enums';
import { StatementService } from 'src/app/core/services/utelize/system/statement/statement-service';
import { StorageService } from 'src/app/core/services/utelize/general/storage-service';
import { SystemUserDTO } from 'src/app/core/models/system/user/system-user.dto';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetRowUsageDashboardDistributionComponent } from 'src/app/shared/widget-rows/widget-row-usage-dashboard/widget-row-usage-dashboard-distribution/widget-row-usage-dashboard-distribution.component';
import { WidgetRowUsageDashboardStatsComponent } from 'src/app/shared/widget-rows/widget-row-usage-dashboard/widget-row-usage-dashboard-stats/widget-row-usage-dashboard-stats.component';
import { WidgetRowUsageDashboardSummaryComponent } from 'src/app/shared/widget-rows/widget-row-usage-dashboard/widget-row-usage-dashboard-summary/widget-row-usage-dashboard-summary.component';
import { WidgetRowUsageDashboardTopSimsComponent } from 'src/app/shared/widget-rows/widget-row-usage-dashboard/widget-row-usage-dashboard-top-sims/widget-row-usage-dashboard-top-sims.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-usage-dashboard',
  templateUrl: './usage-dashboard.component.html',
  styleUrls: ['./usage-dashboard.component.scss'],
  providers: [DatePipe, TippyToolTipService]
})

export class UsageDashboardComponent implements AfterViewInit, OnDestroy, OnInit {

  @ViewChild('widgetRowDistribution', { static: false }) widgetRowDistribution!: WidgetRowUsageDashboardDistributionComponent;
  @ViewChild('widgetRowStats',        { static: false }) widgetRowStats!       : WidgetRowUsageDashboardStatsComponent;
  @ViewChild('widgetRowSummary',      { static: false }) widgetRowSummary!     : WidgetRowUsageDashboardSummaryComponent;
  @ViewChild('widgetRowTopSims',      { static: false }) widgetRowTopSims!     : WidgetRowUsageDashboardTopSimsComponent;

  get platformInviewMode() { return this.isPlatformModeInview ? this.platformInviewService.getMode() : null; }

  get companyTopLevelSelect() { return this.componentAccessUserService.hasAccessGuid(AccessGuids.DropdownsCompanyTopLevelSelect, true); }

  get changeTitles            () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardChangeTitles,                 true ); }
  get showCompanyForUser      () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardShowCompanyForUser,           false); }
  get showDateSelector        () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardShowDateSelector,             false); }
  get showDateSelectorViaIcon () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardShowDateSelectorViaIconClick, false)
                                          || this.curUser?.systemAdmin                                                                               ; }
  get statementDownloadEnabled() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DashboardStatementDownloadEnabled,     false); }
  get superHierarchyEnabled   () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.DropdownCompanySuperHierarchyEnabled,  false); }

  get changeTitleDelay() { return this.configSystemService.getValueAsInteger(ConfigSystemParams.DashboardChangeTitleDelay, WriteToTemplateDelay.FiveMins); }

  private destroy$=new Subject<void>();
  
  private titleTimeout: ReturnType<typeof setTimeout> | undefined;

  public breadCrumbItems!      : Array<{}>;
  public curUser!              : SystemUserDTO;
  public filterCompanyID       : number  | undefined;
  public filterDate            : OurDate | undefined;
  public filterDateFormat      : string=FlatpickrFormat.MonthWordFull_Year;
  public dropdownReady         : boolean=false;
  public isPlatformModeInview  : boolean=environment.platformModeInview;
  public isReseller            : boolean=false;
  public lastUpdatedText       : string="";
  public _showDateSelector     : boolean=false;
  public statementDownloading  : boolean=false;
  public summaryLeadingTemplate: TemplateRef<unknown> | undefined;
  public title                 : string="SureSIM";

  constructor(
    private authenticationService     : AuthenticationService,
    private changeDetectorRef         : ChangeDetectorRef,
    private componentAccessUserService: ComponentAccessUserService,
    private configSystemService       : ConfigSystemService,
    private datePipe                  : DatePipe,
    private dropdownService           : DropdownService,
    private exceptionsService         : ExceptionsService,
    private platformInviewService     : PlatformInviewService,
    private statementService          : StatementService,
    private storageService            : StorageService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.curUser          =this.authenticationService.currentUserObject();
      const trackedCompanyId=this.dropdownService.trackerCompanyId ?? 0;
      this.filterCompanyID  =trackedCompanyId!==0
                             ? trackedCompanyId
                             : (this.companyTopLevelSelect ? 0 - this.curUser.companyId : this.curUser.companyId);
 
      this.filterDate       =populateOurDate(this.exceptionsService, Date.now());
      this.breadCrumbItems  =[{ label: 'Dashboards' }, { label: 'Usage', active: true }];

      this.setTitle();

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.dropdownReady=true;
        this.changeDetectorRef.markForCheck();
      });

      this.widgetRowStats?.emitSelection       (this.filterCompanyID, this.filterDate);
      this.widgetRowDistribution?.emitSelection(this.filterCompanyID, this.filterDate, this.isReseller);
      this.widgetRowSummary?.emitSelection     (this.filterCompanyID, this.filterDate);
      this.widgetRowTopSims?.emitSelection     (this.filterCompanyID, this.filterDate);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();

      if (this.titleTimeout!=undefined)
        clearTimeout(this.titleTimeout);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onCompanyDropDownChange(companyID: number): void {
    try {
      this.filterCompanyID=!this.superHierarchyEnabled &&
                           (companyID==null ||
                            companyID==undefined ||
                            companyID<=0)
                           ?
                           0 - this.curUser.companyId
                           :
                           companyID;

      this.dropdownService.trackerCompanyId=this.filterCompanyID;

      //RBW_20260507 - reset isReseller until the secondary event resolves it. The Cost Breakdown by Zone widget renders for resellers regardless of bundle count, so it must default to false on a fresh selection
      this.isReseller=false;

      this.widgetRowStats?.emitSelection       (this.filterCompanyID, this.filterDate);
      this.widgetRowDistribution?.emitSelection(this.filterCompanyID, this.filterDate, this.isReseller);
      this.widgetRowSummary?.emitSelection     (this.filterCompanyID, this.filterDate);
      this.widgetRowTopSims?.emitSelection     (this.filterCompanyID, this.filterDate);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onFilterDateChange(selectedDate: OurDate): void {
    try {
      this.filterDate=selectedDate;

      this.widgetRowStats       ?.emitSelection(this.filterCompanyID, this.filterDate                 );
      this.widgetRowDistribution?.emitSelection(this.filterCompanyID, this.filterDate, this.isReseller);
      this.widgetRowSummary     ?.emitSelection(this.filterCompanyID, this.filterDate                 );
      this.widgetRowTopSims     ?.emitSelection(this.filterCompanyID, this.filterDate                 );

      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public showDateSelectorClick(): void {
    try {
      if (this._showDateSelector || this.showDateSelector) {
        this._showDateSelector=false;
        this.onFilterDateChange(<OurDate>populateOurDate(this.exceptionsService, Date.now()));
        return;
      }

      this._showDateSelector=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public downloadStatement(): void {
    try {
      const companyID=this.filterCompanyID;
      const year     =Number(this.filterDate?.year);
      const month    =Number(this.filterDate?.month);

      if (companyID==undefined || companyID===0 || !year || !month) {
        this.exceptionsService.handleException(new Error('UsageDashboardComponent.downloadStatement() - A company and period must be selected.'));
        return;
      }

      const scope=this.isReseller ? StatementScope.Hierarchy : StatementScope.CompanyOnly;

      this.statementDownloading=true;

      this.statementService.downloadCompanyStatement(companyID, year, month, scope)
          .pipe(finalize(() => this.statementDownloading=false), takeUntil(this.destroy$))
          .subscribe({
            error: error => { this.exceptionsService.handleException(new Error(error)); }
          });
    }
    catch(e) {
      this.statementDownloading=false;
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onDistributionRightPanelMovedChange(moved: boolean): void {
    try {
      this.summaryLeadingTemplate=moved ? this.widgetRowDistribution?.rightPanelTemplate : undefined;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onCompanyDataSaved(): void {
    try {
      this.widgetRowStats?.refresh();
      this.widgetRowDistribution?.refresh();
      this.widgetRowSummary?.refresh();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public onCompanyDropDownChange2(event: CompanySelectEvent): void {
    try {
      this.isReseller=event?.isReseller ?? false;

      //RBW_20260507 - the distribution widget debounces+dedupes by companyId+date so this re-emit only refreshes the isReseller flag, not the data fetch
      this.widgetRowDistribution?.emitSelection(this.filterCompanyID, this.filterDate, this.isReseller);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#region Header subtitle

  public setLoginTitle(): void {
    try {
      const lastLogin=new Date(<Date>this.curUser.lastLogin);

      if (lastLogin.getFullYear()<2000)
        this.lastUpdatedText=`Statistics at a glance`;
      else
        this.lastUpdatedText=`Last login - ${this.datePipe.transform(lastLogin, 'EEEE dd/MM/yyyy HH:mm:ss')}`;

      this.storageService.setItem(StorageValues.encLoginTitleSet, EncryptAesClientSide('true'));
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public setTitle(): void {
    try {
      if (DecryptAesClientSide(this.storageService.getItem(StorageValues.encLoginTitleSet))) {
        this.setLoginTitle();

      } else {
        this.lastUpdatedText="Real-time overview";

        if (this.changeTitles) {
          this.titleTimeout=setTimeout(() => {
            this.setLoginTitle();
          }, this.changeTitleDelay);
        }
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion
}
